import type { UseMutationResult } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useTranslate } from "@similarweb/i18n-provider";

import salesApiService from "app/pages/sales-intelligence/services/salesApiService";
import { downloadFile } from "app/pages/sales-intelligence/utils/downloadFile";
import { trackError } from "app/pages/sales-intelligence/utils/errors/tracking";
import { SiError, errorCategoryMap } from "app/pages/sales-intelligence/utils/errors/types";

import { useSignalRecommenderToasts } from "../../hooks/useSignalRecommenderToasts";
import signalRecommenderI18nKeys from "../../utils/i18n";
import { signalRecommenderQueryKeys } from "../key-factory/factory";

type DownloadVariables = {
    readonly id: number;
    readonly fileName: string;
};

export const useDownloadDealSignalFileMutation = (): UseMutationResult<string, Error, DownloadVariables> => {
    const { translate } = useTranslate();
    const queryClient = useQueryClient();
    const { showError } = useSignalRecommenderToasts();

    return useMutation<string, Error, DownloadVariables>({
        mutationFn({ id }) {
            return salesApiService.signalRecommender.fetchFileDownloadLink(id);
        },

        onSuccess(link, { fileName }) {
            downloadFile(fileName, link);
        },

        onError(error) {
            showError(translate(signalRecommenderI18nKeys.toast.downloadError));

            trackError(
                new SiError(
                    "Failed to fetch the deal signals download link",
                    { category: errorCategoryMap.api, operation: "downloadDealSignalFile" },
                    { cause: error },
                ),
            );
        },

        // On success the server has marked the file downloaded, so the row would keep a stale "new" badge.
        // On failure the row is stale in a worse way: a 404 here means the file is no longer downloadable,
        // yet the cached row still renders as succeeded, so every retry fails the same way. Refetching on
        // both outcomes is what lets the row correct itself.
        onSettled() {
            void queryClient.invalidateQueries({ queryKey: signalRecommenderQueryKeys.files() });
        },
    });
};
